// Optional room challenges: a seeded bonus objective offered in combat/elite rooms.
// The tracker listens to bus events, exposes a ChallengeState for the HUD/terminal,
// resolves on success/failure and grants its reward.

import type { ChallengeState, GameContext, Vec } from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import type { Rng } from '../../core/rng';
import type { NodeType, OrbType } from '../../core/types';

export type ChallengeId = 'untouchable' | 'blitz' | 'aerialist' | 'conductor';

interface ChallengeDef {
  id: ChallengeId;
  name: string;
  /** Kill target for counting challenges. */
  goal?: number;
  rewardText: string;
}

export const CHALLENGES: Readonly<Record<ChallengeId, ChallengeDef>> = {
  untouchable: { id: 'untouchable', name: 'UNTOUCHABLE', rewardText: 'BONUS ORB' },
  blitz: { id: 'blitz', name: 'BLITZ', rewardText: '+40 SCRAP' },
  aerialist: { id: 'aerialist', name: 'AERIALIST', goal: 5, rewardText: 'UNSTABLE ORB' },
  conductor: { id: 'conductor', name: 'CONDUCTOR', goal: 4, rewardText: 'BONUS ORB' },
};

/** Share of combat/elite rooms that offer a challenge. */
const CHALLENGE_CHANCE = 0.6;

/** Seeded pick (null = no challenge in this room). Conductor only with an electric build. */
export function rollChallenge(rng: Rng, type: NodeType, hasElectric: boolean): ChallengeId | null {
  if (type !== 'combat' && type !== 'elite') return null;
  if (!rng.chance(CHALLENGE_CHANCE)) return null;
  const pool: [ChallengeId, number][] = [
    ['untouchable', 1],
    ['blitz', 1.2],
    ['aerialist', 1],
    ['conductor', hasElectric ? 1 : 0],
  ];
  return rng.weighted(pool, (p) => p[1])?.[0] ?? null;
}

export class ChallengeTracker {
  readonly state: ChallengeState;
  private readonly def: ChallengeDef;
  private subs = new Subscriptions();
  private count = 0;
  private lastHp = Infinity;
  private running = false;

  constructor(
    private ctx: GameContext,
    id: ChallengeId,
    private opts: {
      /** Blitz time limit in seconds. */
      limit?: number;
      /** Where rewards appear. */
      rewardAt: () => Vec;
      onResolved?: (completed: boolean) => void;
    },
  ) {
    this.def = CHALLENGES[id];
    this.state = { id, text: this.describe(), status: 'active' };
    if (id === 'blitz') this.state.timeLeft = opts.limit ?? 45;
  }

  get id(): ChallengeId {
    return this.def.id;
  }

  get name(): string {
    return this.def.name;
  }

  get rewardText(): string {
    return this.def.rewardText;
  }

  private describe(): string {
    switch (this.def.id) {
      case 'untouchable':
        return 'UNTOUCHABLE // CLEAR WITHOUT LOSING HULL';
      case 'blitz':
        return `BLITZ // CLEAR WITHIN ${Math.round(this.opts.limit ?? 45)}s`;
      case 'aerialist':
        return `AERIALIST // AIRBORNE KILLS ${this.count}/${this.def.goal}`;
      case 'conductor':
        return `CONDUCTOR // ELECTRIC KILLS ${this.count}/${this.def.goal}`;
    }
  }

  /** Called when combat starts. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastHp = this.ctx.player.hp;
    const { bus } = this.ctx;
    this.subs.add(
      bus.on('player:damaged', (e) => {
        if (this.def.id === 'untouchable' && e.hp < this.lastHp - 0.01) this.resolve(false);
      }),
    );
    this.subs.add(
      bus.on('enemy:killed', (e) => {
        if (e.enemy.kind === 'prop' || this.state.status !== 'active') return;
        if (this.def.id === 'aerialist') {
          if (e.info.source !== 'player' || this.ctx.player.onGround) return;
        } else if (this.def.id === 'conductor') {
          if (!e.info.tags.includes('electric')) return;
        } else return;
        this.count++;
        this.state.text = this.describe();
        if (this.count >= (this.def.goal ?? 1)) this.resolve(true);
      }),
    );
    bus.emit('challenge:started', { id: this.def.id, text: this.state.text });
  }

  update(dt: number): void {
    if (!this.running || this.state.status !== 'active') return;
    this.lastHp = this.ctx.player.hp;
    if (this.state.timeLeft !== undefined) {
      this.state.timeLeft = Math.max(0, this.state.timeLeft - dt);
      if (this.state.timeLeft <= 0) this.resolve(false);
    }
  }

  /** The room was cleared: timed / no-hit challenges succeed, unfinished counters fail. */
  onRoomCleared(): void {
    if (this.state.status !== 'active') return;
    this.resolve(this.def.id === 'untouchable' || this.def.id === 'blitz');
  }

  private resolve(completed: boolean): void {
    if (this.state.status !== 'active') return;
    this.state.status = completed ? 'completed' : 'failed';
    this.subs.dispose();
    const { ctx } = this;
    const payload = { id: this.def.id, text: this.state.text };
    if (completed) {
      ctx.run.stats.challengesCompleted++;
      ctx.bus.emit('challenge:completed', payload);
      ctx.audio.play('challenge_complete');
      ctx.bus.emit('ui:toast', { text: `CHALLENGE COMPLETE // ${this.def.name} // ${this.def.rewardText}`, color: 0x5dffa8 });
      this.grantReward();
    } else {
      ctx.bus.emit('challenge:failed', payload);
      ctx.audio.play('challenge_fail');
      ctx.bus.emit('ui:toast', { text: `CHALLENGE FAILED // ${this.def.name}`, color: 0xff5d73 });
    }
    this.opts.onResolved?.(completed);
  }

  private grantReward(): void {
    const at = this.opts.rewardAt();
    const loot = this.ctx.loot;
    switch (this.def.id) {
      case 'blitz':
        loot.spawnScrap(at.x, at.y, 40);
        break;
      case 'aerialist':
        loot.spawnOrb('unstable', at.x, at.y, { source: 'challenge', dropIn: true });
        break;
      default: {
        const type: OrbType = this.ctx.rng.weighted<OrbType>(['arsenal', 'armor', 'unstable'], (t) =>
          t === 'arsenal' ? 40 : t === 'armor' ? 45 : 15,
        )!;
        loot.spawnOrb(type, at.x, at.y, { source: 'challenge', dropIn: true });
      }
    }
  }

  destroy(): void {
    this.subs.dispose();
    this.running = false;
  }
}
